import { templatesIndex } from 'api/algolia';
import { TEMPLATES_SEARCH } from 'constants/mixpanel';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import request from 'utils/request';

import {
  DEFAULT_HITS_PER_PAGE,
  E2_TEMPLATE_CATEGORY,
  MAX_NUM_SEARCH_RESULTS,
  algoliaEmptyResponse,
} from './usePaginatedTemplatesSearch.config';
import {
  InfographTemplate,
  Layout,
  PageSize,
  SearchApiResponse,
  TemplateFilters,
  TemplateHit,
} from './usePaginatedTemplatesSearch.types';

const BASE_PATH = '/template_market/index/fetchTemplates';

/**
 * Parse response from infograph fetchTemplates endpoint to Algolia response
 * @param infographTemplates Response from infograph fetchTemplates endpoint
 * @returns
 */

export function parseInfographToAlgoliaResponse(infographTemplates: InfographTemplate[]): TemplateHit[] {
  return infographTemplates.map((template) => {
    const templateType: TemplateHit['type'] = (
      template.permission === 'default' ? 'free' : template.permission
    ) as TemplateHit['type'];

    return {
      objectID: template.id,
      template_id: template.id,
      title: template.title,
      type: templateType,
      isMultipage: template?.pages > 1 ? 1 : 0,
      width: template.width,
      height: template.height,
      e2TemplateEnabled: template.editor2,
      categories: [template.category],

      // Not used but are included in the algolia records
      ...algoliaEmptyResponse,
    };
  });
}

/**
 * Perform a request to fetch templates from infograph, the result will be the same as the one that we use in infograph templates page.
 *
 * @param url infograph fetchTemplates endpoint
 * @param filters filters to apply to the request
 *  - planFilters: plan type filters array of strings
 *  - isAccessible: boolean to filter only accessible templates
 *  - there is a third filter `sizeFilters` that is supported in infograph API but we dont need it here. The type is an array of strings
 * @returns
 */

const fetchInfographTemplates = async (
  url: string,
  filters: {
    planFilters: string[];
    isAccessible?: boolean;
  },
) => {
  const planFilters = filters.planFilters.map((item) => (item === 'free' ? 'default' : item));

  const otherFilters: Record<string, any> = {
    only_e2: true,
  };

  if (filters.isAccessible) {
    otherFilters['accessibility'] = 'enabled';
  }

  return await request<{
    templates: InfographTemplate[];
  }>(url, {
    method: 'POST',
    body: JSON.stringify({
      filters: {
        planFilters,
        sizeFilters: [],
        otherFilters,
      },
    }),
  });
};

/**
 * Perform Algolia search for templates
 * For search query, fetch the max number of results (MAX_NUM_SEARCH_RESULTS)
 * Otherwise, use DEFAULT_HITS_PER_PAGE for pagination
 *
 * @param templateFilters Filters to apply to search query - only `isAccessible` filter option is used here
 *  Other filters are applied manually when parsing results (see parseTemplates fn)
 * @returns
 */
export const fetchTemplates =
  (templateFilters?: TemplateFilters, useInfographTemplates = false) =>
  (key: string) => {
    const [query, page] = key.split('_');
    const { venngageE2 } = useVenngageE1Gate();
    const templateCategory = venngageE2?.templateCategory?.category;
    const templateSubCategory = venngageE2?.templateCategory?.subcategory;
    // If query is specified (i.e. user is searching), fetch up to max number of search results
    // Similar to E1 implementation - required as a workaround for template filtering
    const hitsPerPage = !query ? DEFAULT_HITS_PER_PAGE : MAX_NUM_SEARCH_RESULTS;
    let filters = `e2TemplateEnabled:true`;

    // Apply accessibility + plan type filters if specified
    if (templateFilters) {
      if (!!templateFilters.isAccessible) {
        filters += ` AND categories:${E2_TEMPLATE_CATEGORY}`;
      }

      if (!!templateFilters.planType && templateFilters.planType.length > 0) {
        const planTypeFilters = templateFilters.planType.map((planType) => `type:${planType}`);
        filters += ` AND (${planTypeFilters.join(' OR ')})`;
      }

      if (Object.keys(templateFilters).length === 0 && query === '') {
        if (templateSubCategory) {
          const filterBySubCategory = ` AND (subcategories:"${templateSubCategory.toLowerCase()}")`;
          filters += filterBySubCategory;
        }

        if (templateCategory) {
          const filterByCategory = ` OR (categories:"${templateCategory.toLowerCase()}")`;
          filters += filterByCategory;
        }
      }
    }

    if (useInfographTemplates) {
      return fetchInfographTemplates(
        `${BASE_PATH}?offset=${Number(page) + 1}&category=${templateCategory || 'infographics'}`,
        {
          planFilters: templateFilters?.planType || [],
          isAccessible: !!templateFilters?.isAccessible,
        },
      ).then((content) => {
        const numberOfPages = content?.templates?.length ? Number(page + 1) : 0;
        const currentPage = Number(page + 1);

        return {
          hits: parseInfographToAlgoliaResponse(content?.templates || []),
          nbPages: numberOfPages,
          page: currentPage,
        };
      });
    }

    return templatesIndex.search(query, { hitsPerPage, filters, page: +page }).then(async (content) => {
      type ContentType<T> = T extends Promise<infer U> ? U : never;

      let result: ContentType<ReturnType<typeof templatesIndex.search>> = content;

      if (Array.isArray(content.hits) && content.hits.length === 0) {
        //fallback results, add algolia's `similarQuery` option to get similar results
        const fallbackResults = (await new Promise((resolve) => {
          templatesIndex
            .search(query, { similarQuery: query, hitsPerPage, filters, page: +page })
            .then(async (fallback) => {
              return resolve(fallback);
            });
        })) as ContentType<ReturnType<typeof templatesIndex.search>>;

        result = fallbackResults;
      }

      // Trigger MP event after a successful search request to obtain
      // the number of results returned
      if (query && +page === 0) {
        Mixpanel.track(TEMPLATES_SEARCH.eventName, {
          query,
          'number of results': result.nbHits,
        });
      }

      return result;
    });
  };

/**
 * Returns true if any filters are applied
 *
 * @param filters
 * @returns boolean
 */
export const checkHasFiltersApplied = (filters: TemplateFilters | null) => {
  if (!filters) return false;
  if (filters.planType && filters.planType.length > 0) return true;
  if (filters.pageSize && filters.pageSize.length > 0) return true;
  if (filters.layout && filters.layout.length > 0) return true;
  if (filters.isAccessible) return true;

  return false;
};

const flattenHits = (d: SearchApiResponse) => d.hits;

/**
 * Parses templates search response from algolia and applies the specified filters if any
 *
 * @param data
 * @param filterOptions
 * @returns
 */
export const parseTemplates = (data: SearchApiResponse[], filterOptions?: TemplateFilters) => {
  if (!data) return [];

  const { pageSize, layout } = filterOptions || {};
  const hasPageSizeFilter = Array.isArray(pageSize) && pageSize.length > 0;
  const hasLayoutFilter = Array.isArray(layout) && layout.length > 0;

  return data
    .filter(Boolean)
    .flatMap(flattenHits)
    .filter((templateHit) => {
      // If no filters are applied, return all templates
      if (!filterOptions) return true;

      // If page size or layout filters aren't applied, return all templates
      // The other filters are applied directly by algolia
      if (!hasPageSizeFilter && !hasLayoutFilter) return true;

      const { width, height } = templateHit;

      let isPageSizeMatch = !hasPageSizeFilter;
      let isLayoutMatch = !hasLayoutFilter;

      // Check page size filter
      if (hasPageSizeFilter && width && height) {
        const isLetter = isLetterSize(width, height);
        if (pageSize.includes(PageSize.Letter) && isLetter) {
          isPageSizeMatch = true;
        } else if (pageSize.includes(PageSize.Square) && width === height) {
          isPageSizeMatch = true;
        } else if (pageSize.includes(PageSize.Custom) && !isLetter) {
          // Custom size is any size that is not letter (same as templates page)
          isPageSizeMatch = true;
        }
      }

      // Check layout filter
      if (hasLayoutFilter) {
        if (layout.includes(Layout.Portrait) && height > width) {
          isLayoutMatch = true;
        } else if (layout.includes(Layout.Landscape) && width > height) {
          isLayoutMatch = true;
        }
      }

      return isPageSizeMatch && isLayoutMatch;
    });
};

/**
 * Check if a template is letter size
 *
 * @param width
 * @param height
 * @returns boolean
 */
const LETTER_SIZE = { width: 816, height: 1056 };
export const isLetterSize = (width: number, height: number) =>
  (width === LETTER_SIZE.width && height === LETTER_SIZE.height) ||
  (height === LETTER_SIZE.width && width === LETTER_SIZE.height);
